import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { unwrapKey } from "@timecapsule/crypto";
import { api, type Follow, type Me, type Member, type PersonEntry } from "./api";
import { KeyDialog } from "./KeyDialog";
import { ReauthDialog } from "./ReauthDialog";
import { forgetOldKeys } from "./keyring";
import { passkeyPrf } from "./passkey";
import { deviceKey, type DeviceKey } from "./storage";
import { errorText, msg } from "./i18n";
import { Failed, Loading } from "./Loading";
import { useRefreshHandler } from "./Refresh";

/** The member closed the PIN dialog: the action that asked for the key does not run. */
export class KeyRequestCancelled extends Error {
  constructor() {
    super("Cancelled.");
    this.name = "KeyRequestCancelled";
  }
}

export const isKeyRequestCancelled = (err: unknown) => err instanceof KeyRequestCancelled;

type KeyRequest = { purpose: string; resolve: (r: { key: Uint8Array; held: boolean }) => void; reject: (err: Error) => void };

/**
 * Where the member's key stands on this device:
 *   none      no device copy (new device, or never set up)
 *   mismatch  a device copy of another identity than the account's active one
 *   locked    device copy protected by passkey + PIN and not held in memory; actions ask for it (withKey)
 *   unlocked  key held in memory ("Unlock for … minutes"), or a "plain" copy, which is always open
 */
export type KeyStatus = "none" | "mismatch" | "locked" | "unlocked";

type Session = {
  me: Me;
  /** Secret key in memory, only while unlocked and only if it is the account's active identity. */
  secretKey: Uint8Array | null;
  keyStatus: KeyStatus;
  device: DeviceKey | null;
  /**
   * Members this page knows (PLAN.md 4.20 step 5): me, whom I follow, my followers, blocks and hides,
   * search results and lookups. There is no full directory; rows ask for the names they show.
   */
  memberById: (id: number) => Member | undefined;
  /** The member who owns this key, current or earlier. */
  memberByKey: (key: string | null | undefined) => Member | undefined;
  /** Adds members (a search result, a list) to what the page knows. */
  rememberMembers: (list: Member[]) => void;
  /** Asks the server for the members and key owners the page does not know yet; each only once until Refresh. */
  resolveMembers: (want: { ids?: number[]; keys?: string[] }) => void;
  /** Whom I may send a private capsule: me and the members I follow (PLAN.md 4.14), with their keys. */
  reachable: Member[];
  /** Every key of this account, current and earlier (PLAN.md 4.16 K4): capsules to any of them are mine. */
  myKeys: ReadonlySet<string>;
  nameOf: (pubkey: string | null) => string;
  nameOfId: (id: number) => string;
  /**
   * Members I follow (PLAN.md 4.14), by member id, and every key of theirs (`follows`, to mark their
   * public capsules); public capsules I watch. Stored on the server so every device sees them.
   */
  followed: Map<number, Follow>;
  follows: Set<string>;
  watches: Set<string>;
  /** Follow or unfollow the member who owns this key (current or retired). */
  toggleFollow: (pubkey: string) => Promise<void>;
  followMember: (memberId: number, follow: boolean) => Promise<void>;
  toggleWatch: (id: string) => Promise<void>;
  /**
   * PLAN.md 4.16 K9, on the server: members I block (no private capsules from them), members I follow
   * who block me (Write warns before sending; 4.20), members I hide (their public capsules leave my Feed).
   */
  blocked: Map<number, PersonEntry>;
  blockedBy: Set<number>;
  hidden: Map<number, PersonEntry>;
  blockMember: (memberId: number, block: boolean) => Promise<void>;
  hideMember: (memberId: number, hide: boolean) => Promise<void>;
  refresh: () => Promise<void>;
  /** Stores a new device copy, keeps its key unlocked and reloads the profile. */
  adoptKey: (record: DeviceKey, secretKey: Uint8Array) => Promise<void>;
  /** "Unlock for … minutes": opens the device copy with passkey + PIN and holds the key. Throws on a wrong PIN or a cancelled prompt. */
  unlock: (pin: string, minutes: number, signal?: AbortSignal) => Promise<void>;
  /** When a held key is wiped (ms); null while nothing is held. */
  unlockedUntil: number | null;
  /**
   * Unlock on demand (4b step 2g): runs `fn` with the key. A held or plain key is used as it is;
   * otherwise the PIN dialog asks for passkey + PIN, `fn` runs once and the key is dropped (unless
   * the member chose to keep it unlocked). Rejects with KeyRequestCancelled when the dialog is closed.
   */
  withKey: <T>(purpose: string, fn: (key: Uint8Array) => Promise<T>) => Promise<T>;
  /**
   * "Confirm it is you" (PLAN.md 4.16 K3, owner: always asked before a key is created or revoked):
   * the member signs in again; resolves when the server renewed the sign-in time. Rejects with
   * KeyRequestCancelled when the dialog is closed. `reuseRecent`: a confirmation from the last two
   * minutes counts, so the new key right after a revocation (Reset key) does not ask twice.
   * `strong` (the default, 4.18 step 5): only the password or a passkey count; without it a Google
   * sign-in does too (an account's very first key).
   */
  confirmIdentity: (purpose: string, options?: { reuseRecent?: boolean; strong?: boolean }) => Promise<void>;
  /**
   * Counts Locks only (the button or the timer). Content opened with the key is shown while this is
   * unchanged, so Lock hides it at once; leaving the page drops it anyway (owner, 2026-10-07).
   */
  lockGeneration: number;
  lockStillValid: (generation: number) => boolean;
  /** The key was used on demand since the last Lock: something opened with it may be on screen, so Lock is offered. */
  usedSinceLock: boolean;
  /** Fetches my follows again (Write opens with the followed members' current keys). */
  reloadFollows: () => Promise<void>;
  lock: () => void;
  /**
   * Counts every lock and unlock (PLAN.md 4.13 R3). Work that decrypts with the key captures it
   * first and keeps its result only if `keyStillValid(generation)` holds when it finishes, so a
   * decrypt that ends after Lock never puts private content back on screen or into a download.
   */
  keyGeneration: number;
  keyStillValid: (generation: number) => boolean;
  /**
   * A key rebuilt from the phrase, handed from onboarding to /restore across a Google re-link
   * (the login changes member, so onboarding unmounts). Memory only.
   */
  pendingKey: Uint8Array | null;
  setPendingKey: (key: Uint8Array | null) => void;
};

const SessionContext = createContext<Session | null>(null);

/** A follow, follower, block or hide as a directory entry. */
const entryMember = ({ member_id, display_name, name, pubkey, keys, key_since, role }: Follow | PersonEntry): Member => ({ id: member_id, display_name, name, pubkey, keys, key_since, role });
/** This account as a directory entry: the active key's profile name, else the account's. */
const meAsMember = (me: Me): Member => ({ id: me.id, display_name: me.profile?.display_name ?? me.display_name, name: me.profile?.name ?? null, pubkey: me.pubkey, keys: me.keys, key_since: null, role: me.role });

export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}

export function SessionProvider({ me: initialMe, children }: { me: Me; children: ReactNode }) {
  const [me, setMe] = useState(initialMe);
  const [device, setDevice] = useState<DeviceKey | null>(null);
  const [unlocked, setUnlocked] = useState<Uint8Array | null>(null);
  const myKeys = useMemo(() => new Set([...me.keys, ...(me.pubkey ? [me.pubkey] : [])]), [me.keys, me.pubkey]);
  // What the page knows about members (PLAN.md 4.20 step 5), by id and by every key.
  const [directory, setDirectory] = useState<{ byId: Map<number, Member>; byKey: Map<string, number> }>(() => ({ byId: new Map(), byKey: new Map() }));
  const directoryRef = useRef(directory);
  directoryRef.current = directory;
  const asked = useRef(new Set<string>());
  const rememberMembers = useCallback((list: Member[]) => {
    if (list.length === 0) return;
    setDirectory((current) => {
      const byId = new Map(current.byId);
      const byKey = new Map(current.byKey);
      for (const member of list) {
        byId.set(member.id, member);
        for (const key of [...(member.keys ?? []), ...(member.pubkey ? [member.pubkey] : [])]) byKey.set(key, member.id);
      }
      return { byId, byKey };
    });
  }, []);
  const resolveMembers = useCallback(({ ids = [], keys = [] }: { ids?: number[]; keys?: string[] }) => {
    const known = directoryRef.current;
    const wantIds = [...new Set(ids)].filter((id) => !known.byId.has(id) && !asked.current.has(`i${id}`));
    const wantKeys = [...new Set(keys)].filter((key) => !known.byKey.has(key) && !asked.current.has(`k${key}`));
    for (const id of wantIds) asked.current.add(`i${id}`);
    for (const key of wantKeys) asked.current.add(`k${key}`);
    const want: ({ id: number } | { key: string })[] = [...wantIds.map((id) => ({ id })), ...wantKeys.map((key) => ({ key }))];
    for (let i = 0; i < want.length; i += 100) {
      const chunk = want.slice(i, i + 100);
      void api
        .lookupMembers(chunk.flatMap((w) => ("id" in w ? [w.id] : [])), chunk.flatMap((w) => ("key" in w ? [w.key] : [])))
        .then(rememberMembers, () => undefined);
    }
  }, [rememberMembers]);
  const memberById = useCallback((id: number) => directory.byId.get(id), [directory]);
  const memberByKey = useCallback((key: string | null | undefined) => {
    const id = key ? directory.byKey.get(key) : undefined;
    return id === undefined ? undefined : directory.byId.get(id);
  }, [directory]);
  const [follows, setFollows] = useState<Set<string>>(new Set());
  const [followed, setFollowed] = useState<Map<number, Follow>>(new Map());
  const applyFollows = (r: { follows: Follow[]; authors: string[] }) => {
    setFollows(new Set(r.authors));
    setFollowed(new Map(r.follows.map((f) => [f.member_id, f])));
    // Who blocks me matters only for whom I can write to, the members I follow (PLAN.md 4.20).
    setBlockedBy(new Set(r.follows.filter((f) => f.blocks_me).map((f) => f.member_id)));
    rememberMembers(r.follows.map(entryMember));
  };
  const [watches, setWatches] = useState<Set<string>>(new Set());
  const [blocked, setBlocked] = useState<Map<number, PersonEntry>>(new Map());
  const [blockedBy, setBlockedBy] = useState<Set<number>>(new Set());
  const [hidden, setHidden] = useState<Map<number, PersonEntry>>(new Map());
  const applyBlocks = (r: { blocks: PersonEntry[] }) => {
    setBlocked(new Map(r.blocks.map((b) => [b.member_id, b])));
    rememberMembers(r.blocks.map(entryMember));
  };
  const applyHides = (list: PersonEntry[]) => {
    setHidden(new Map(list.map((h) => [h.member_id, h])));
    rememberMembers(list.map(entryMember));
  };
  const [loaded, setLoaded] = useState(false);
  const [pendingKey, setPendingKey] = useState<Uint8Array | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generation = useRef(0);
  const [keyGeneration, setKeyGeneration] = useState(0);
  const bump = useCallback(() => setKeyGeneration(++generation.current), []);
  const locks = useRef(0);
  const [lockGeneration, setLockGeneration] = useState(0);
  const [unlockedUntil, setUnlockedUntil] = useState<number | null>(null);
  const [usedSinceLock, setUsedSinceLock] = useState(false);
  // The held key for withKey, which must see it without waiting for a render.
  const held = useRef<Uint8Array | null>(null);
  const [request, setRequest] = useState<KeyRequest | null>(null);
  const [reauth, setReauth] = useState<{ purpose: string; strong: boolean; resolve: (strong: boolean) => void; reject: (err: unknown) => void } | null>(null);
  // The last confirmation: when, and whether by password or passkey.
  const confirmed = useRef({ at: 0, strong: false });
  const confirmIdentity = useCallback(
    (purpose: string, { reuseRecent = false, strong = true }: { reuseRecent?: boolean; strong?: boolean } = {}) =>
      reuseRecent && Date.now() - confirmed.current.at < 120_000 && (confirmed.current.strong || !strong)
        ? Promise.resolve()
        : new Promise<void>((resolve, reject) =>
            setReauth({ purpose, strong, resolve: (byStrong) => { confirmed.current = { at: Date.now(), strong: byStrong }; resolve(); }, reject }),
          ),
    [],
  );
  const pending = useRef<KeyRequest | null>(null);

  const lock = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    held.current = null;
    forgetOldKeys();
    setUnlocked(null);
    setUnlockedUntil(null);
    setUsedSinceLock(false);
    setLockGeneration(++locks.current);
    bump();
  }, [bump]);

  /** Keeps the key in memory for `minutes`, then wipes it. */
  const hold = useCallback(
    (secretKey: Uint8Array, minutes: number) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(lock, minutes * 60_000);
      held.current = secretKey;
      setUnlocked(secretKey);
      setUnlockedUntil(Date.now() + minutes * 60_000);
      bump();
    },
    [lock, bump],
  );

  useEffect(() => () => lock(), [lock]);

  // The member id can change during the session: a Google re-link moves this login to the member who owns the identity.
  const refresh = useCallback(async () => {
    const fresh = await api.me();
    if (!fresh) {
      location.reload();
      return;
    }
    const [stored, followed, watched, blocks, hides] = await Promise.all([deviceKey.load(fresh.id), api.follows(), api.watchIds(), api.blocks(), api.hides()]);
    setMe(fresh);
    // Names may have changed: what was asked before is asked again when a row needs it.
    asked.current.clear();
    rememberMembers([meAsMember(fresh)]);
    applyFollows(followed);
    setWatches(new Set(watched));
    applyBlocks(blocks);
    applyHides(hides);
    // Preserve the held plain-key reference when storage has not changed, so Refresh does not
    // cancel and restart every message read just because IndexedDB returned a cloned byte array.
    setDevice((previous) => previous?.kind === "plain" && stored?.kind === "plain" && previous.pubkey === stored.pubkey && previous.secretKey.length === stored.secretKey.length && previous.secretKey.every((byte, index) => byte === stored.secretKey[index]) ? previous : stored ?? null);
    setLoaded(true);
  }, []);

  // The first load can fail (server unreachable): say so with a way out instead of "Loading…" forever.
  useRefreshHandler(refresh);
  const [loadError, setLoadError] = useState<string | null>(null);
  useEffect(() => {
    refresh().catch((err: unknown) => setLoadError(errorText(err)));
  }, [refresh]);

  // A new device copy is not held: the key is asked for when an action needs it (4b step 2g).
  const adoptKey = useCallback(
    async (record: DeviceKey, _secretKey: Uint8Array) => {
      await deviceKey.save(me.id, record);
      await refresh();
    },
    [me.id, refresh],
  );

  const unlock = useCallback(
    async (pin: string, minutes: number, signal?: AbortSignal) => {
      if (device?.kind !== "prf-pin") return;
      const prf = await passkeyPrf(device.copy.credentialId!, signal);
      hold(await unwrapKey(device.copy, { prf, pin }), minutes);
    },
    [device, hold],
  );

  const withKey = useCallback(
    async function <T>(purpose: string, fn: (key: Uint8Array) => Promise<T>): Promise<T> {
      if (!device || device.pubkey !== me.pubkey) {
        throw new Error(device ? msg().errors.keyMismatch : msg().errors.keyMissing);
      }
      if (device.kind === "plain") return fn(device.secretKey);
      if (held.current) return fn(held.current);
      if (pending.current) throw new Error(msg().errors.pinBusy);
      const got = await new Promise<{ key: Uint8Array; held: boolean }>((resolve, reject) => {
        pending.current = { purpose, resolve, reject };
        setRequest(pending.current);
      });
      setUsedSinceLock(true);
      try {
        return await fn(got.key);
      } finally {
        // Used once: overwrite the bytes so the key does not linger in memory.
        if (!got.held) got.key.fill(0);
      }
    },
    [device, me.pubkey],
  ) as Session["withKey"];

  const answer = (key: Uint8Array, holdMinutes: number | null) => {
    const r = pending.current;
    pending.current = null;
    setRequest(null);
    if (holdMinutes) hold(key, holdMinutes);
    r?.resolve({ key, held: !!holdMinutes });
  };
  const cancelRequest = () => {
    const r = pending.current;
    pending.current = null;
    setRequest(null);
    r?.reject(new KeyRequestCancelled());
  };

  const reloadFollows = useCallback(async () => applyFollows(await api.follows()), []);

  const toggleFollow = useCallback(
    async (pubkey: string) => {
      if (follows.has(pubkey)) await api.unfollow(pubkey);
      else await api.follow(pubkey);
      applyFollows(await api.follows());
    },
    [follows],
  );

  const followMember = useCallback(async (memberId: number, follow: boolean) => {
    if (follow) await api.follow(memberId);
    else await api.unfollow(memberId);
    applyFollows(await api.follows());
  }, []);

  const blockMember = useCallback(async (memberId: number, block: boolean) => {
    await api.setBlock(memberId, block);
    applyBlocks(await api.blocks());
  }, []);

  const hideMember = useCallback(async (memberId: number, hide: boolean) => {
    await api.setHide(memberId, hide);
    applyHides(await api.hides());
  }, []);

  const toggleWatch = useCallback(
    async (id: string) => {
      if (watches.has(id)) await api.unwatch(id);
      else await api.watch(id);
      setWatches(new Set(await api.watchIds()));
    },
    [watches],
  );

  const nameOf = useCallback(
    (pubkey: string | null) => {
      if (!pubkey) return msg().common.unknown;
      if (pubkey === me.pubkey) return msg().common.you;
      return memberByKey(pubkey)?.display_name ?? `${pubkey.slice(0, 8)}…`;
    },
    [memberByKey, me.pubkey],
  );
  const nameOfId = useCallback((id: number) => memberById(id)?.display_name ?? `#${id}`, [memberById]);
  const reachable = useMemo(() => [meAsMember(me), ...[...followed.values()].map(entryMember)], [me, followed]);

  // A "plain" device copy is always open; a "prf-pin" copy only while held in memory.
  const inMemory = device?.kind === "plain" ? device.secretKey : unlocked;
  const keyStatus: KeyStatus = !device ? "none" : device.pubkey !== me.pubkey ? "mismatch" : inMemory ? "unlocked" : "locked";
  const secretKey = keyStatus === "unlocked" ? inMemory : null;
  // True while no lock or unlock happened since `g` was taken.
  const keyStillValid = useCallback((g: number) => g === generation.current, []);
  // True while no Lock happened since `g` was taken.
  const lockStillValid = useCallback((g: number) => g === locks.current, []);

  if (loadError && !loaded) return <Failed text={msg().app.cannotReach(loadError)} />;
  if (!loaded) return <Loading />;
  return (
    <SessionContext.Provider
      value={{
        me,
        secretKey,
        keyStatus,
        device,
        memberById,
        memberByKey,
        rememberMembers,
        resolveMembers,
        reachable,
        myKeys,
        nameOf,
        nameOfId,
        followed,
        follows,
        watches,
        toggleFollow,
        followMember,
        blocked,
        blockedBy,
        hidden,
        blockMember,
        hideMember,
        toggleWatch,
        refresh,
        adoptKey,
        unlock,
        unlockedUntil,
        withKey,
        confirmIdentity,
        lock,
        reloadFollows,
        keyGeneration,
        keyStillValid,
        lockGeneration,
        lockStillValid,
        usedSinceLock,
        pendingKey,
        setPendingKey,
      }}
    >
      {children}
      {request && device?.kind === "prf-pin" && <KeyDialog purpose={request.purpose} copy={device.copy} onDone={answer} onCancel={cancelRequest} />}
      {reauth && <ReauthDialog purpose={reauth.purpose} strong={reauth.strong} onDone={(byStrong) => { setReauth(null); reauth.resolve(byStrong); }} onCancel={() => { setReauth(null); reauth.reject(new KeyRequestCancelled()); }} />}
    </SessionContext.Provider>
  );
}
